import { useState, useEffect, useRef } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import OnboardingBanner from "@/components/OnboardingBanner";
import api from "@/lib/api";
import { useAuth } from "@/App";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  DownloadSimple, User, Bell, ShieldCheck, GoogleLogo,
  Warning, CheckCircle, Trash, Buildings, Receipt,
  FileText, FileCsv, Info, LockSimple, Spinner, DeviceMobileCamera,
  UserPlus, Users, X, Sliders, Lightning, Phone, Gift, Copy, Timer, UploadSimple, WhatsappLogo,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CurrentTeam from "@/components/CurrentTeam";
import { useTeamAccess } from "@/lib/queries";
import PermissionEditor, { AccessGrid } from "@/components/PermissionEditor";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// GSTIN: first 2 digits = state code
const GSTIN_STATES = {
  "01":"Jammu & Kashmir","02":"Himachal Pradesh","03":"Punjab","04":"Chandigarh",
  "05":"Uttarakhand","06":"Haryana","07":"Delhi","08":"Rajasthan","09":"Uttar Pradesh",
  "10":"Bihar","11":"Sikkim","12":"Arunachal Pradesh","13":"Nagaland","14":"Manipur",
  "15":"Mizoram","16":"Tripura","17":"Meghalaya","18":"Assam","19":"West Bengal",
  "20":"Jharkhand","21":"Odisha","22":"Chhattisgarh","23":"Madhya Pradesh","24":"Gujarat",
  "25":"Daman & Diu","26":"Dadra & Nagar Haveli","27":"Maharashtra","28":"Andhra Pradesh",
  "29":"Karnataka","30":"Goa","31":"Lakshadweep","32":"Kerala","33":"Tamil Nadu",
  "34":"Puducherry","35":"Andaman & Nicobar","36":"Telangana","37":"Andhra Pradesh (New)",
  "38":"Ladakh",
};

const GSTIN_RE   = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE     = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function gstinChecksum(gstin) {
  let total = 0;
  for (let i = 0; i < 14; i++) {
    const idx = GSTIN_CHARS.indexOf(gstin[i]);
    if (idx === -1) return null;
    const product = idx * (i % 2 === 0 ? 1 : 2);
    total += Math.floor(product / 36) + (product % 36);
  }
  return GSTIN_CHARS[(36 - (total % 36)) % 36];
}

function validateGSTIN(v) {
  if (!v) return null;
  if (v.length !== 15) return "Must be 15 characters";
  if (!GSTIN_RE.test(v)) return "Invalid GSTIN format";
  const expected = gstinChecksum(v);
  if (expected && expected !== v[14]) return `Invalid GSTIN — checksum digit should be ${expected}, not ${v[14]}`;
  return "valid";
}

function validatePAN(v) {
  if (!v) return null;
  if (v.length !== 10) return "Must be 10 characters";
  if (!PAN_RE.test(v)) return "Invalid PAN format (e.g. ABCDE1234F)";
  return "valid";
}

function SectionCard({ icon, title, children }) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
        <span className="text-amber-500">{icon}</span>
        <h2 className="font-manrope text-sm font-semibold text-slate-800 dark:text-white">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <div>
      <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">{label}</Label>
      {hint && <p className="text-[11px] text-slate-400 mb-1">{hint}</p>}
      {children}
    </div>
  );
}

export default function Settings() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", phone: "", pan_number: "", gst_number: "", gst_address: "", advance_non_refundable: false, guest_whatsapp_paused: false, company_name: "", upi_id: "", upi_qr_url: "", same_day_cutoff_time: "",
    notification_prefs: { emi: true, tax: true, maintenance: true, checkin: true },
  });
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingQR, setUploadingQR] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0); // 0=hidden, 1=confirm, 2=typing
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Connect WhatsApp. Signup is email OR phone, never both, so most accounts have no
  // number at all and receive none of the WhatsApp features. This is where that is fixed.
  const [waLink, setWaLink] = useState(null);
  const [waPhone, setWaPhone] = useState("");
  const [waCode, setWaCode] = useState("");
  const [waSent, setWaSent] = useState(false);
  const [waBusy, setWaBusy] = useState(false);

  useEffect(() => {
    api.get("/wa/link")
      .then(r => setWaLink(r.data))
      .catch(() => setWaLink({ connected: false }));
  }, []);

  const waStart = async () => {
    setWaBusy(true);
    try {
      await api.post("/wa/link/start", { phone: waPhone });
      setWaSent(true);
      setWaCode("");
      toast.success("Code sent on WhatsApp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the code");
    } finally { setWaBusy(false); }
  };

  const waVerify = async () => {
    setWaBusy(true);
    try {
      const { data } = await api.post("/wa/link/verify", { code: waCode });
      setWaLink({ connected: true, phone: data.phone });
      setWaSent(false);
      setWaCode("");
      toast.success("WhatsApp connected");
    } catch (e) {
      toast.error(e.response?.data?.detail || "That code did not work");
    } finally { setWaBusy(false); }
  };

  const waRemove = async () => {
    setWaBusy(true);
    try {
      await api.post("/wa/link/remove");
      setWaLink({ connected: false });
      setWaPhone("");
      toast.success("WhatsApp disconnected");
    } catch {
      toast.error("Could not disconnect");
    } finally { setWaBusy(false); }
  };

  // Team management
  const teamQuery = useTeamAccess(user && !user.is_staff ? user.user_id : null);
  const teamMembers = teamQuery.data?.members || [];
  const teamProperties = teamQuery.data?.properties || [];
  const loadTeam = () => teamQuery.refetch();
  const [inviteForm, setInviteForm] = useState({ email: "", phone: "", role: "co_host" });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  // Access set at invite time (2026-09-07): pre-filled from role defaults, host can customise
  const [roleDefaults, setRoleDefaults] = useState(null);
  const [customiseInvite, setCustomiseInvite] = useState(false);
  const [inviteAccess, setInviteAccess] = useState({ permissions: {}, show_revenue: true, assigned_property_ids: [] });
  const applyRoleDefaults = (role, defaults = roleDefaults) => {
    if (!defaults) return;
    setInviteAccess({
      permissions: { ...(defaults.permissions?.[role] || {}) },
      show_revenue: !!defaults.show_revenue?.[role],
      assigned_property_ids: [],
    });
  };

  // Referral program
  const [referralData, setReferralData] = useState(null);

  // Automations
  const [autoForm, setAutoForm] = useState({
    auto_create_cleaning_task: true,
    auto_review_request: true,
    auto_checkin_48h: true,
    auto_checkin_day: true,
    auto_checkout_day: true,
    checkin_whatsapp: true,
    smart_reply_enabled: true,
    auto_daily_digest: true,
    pricing_nudges_enabled: true,
    dnd_enabled: false,
    dnd_start: "23:00",
    dnd_end: "07:00",
  });
  const [savingAuto, setSavingAuto] = useState(false);

  // Dynamic pricing settings
  const [pricingSettings, setPricingSettings] = useState({
    auto_apply_enabled: false,
    auto_apply_max_pct: 20,
    last_minute_enabled: true,
    last_minute_days: 7,
    last_minute_pct: 15,
  });
  const [savingPricing, setSavingPricing] = useState(false);

  // 2FA
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState(null); // null | "awaiting_otp"
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaLoading, setTwoFaLoading] = useState(false);

  const isDirty = original && JSON.stringify(form) !== JSON.stringify(original);

  // Block in-app navigation when there are unsaved changes
  const blocker = useBlocker(isDirty);
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm("You have unsaved changes. Leave without saving?")) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  // Block browser tab close / reload when there are unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const formInitFor = useRef(null);
  useEffect(() => {
    if (user) {
      setTwoFaEnabled(user.two_fa_enabled || false);
      // Only (re)initialize the form on first load or when the account actually changes
      // (workspace switch). Re-initializing on every setUser() silently wiped unsaved
      // edits and flipped the save button to a disabled "Saved" state.
      if (formInitFor.current === user.user_id) return;
      formInitFor.current = user.user_id;
      const initial = {
        name: user.name || "",
        phone: user.phone || "",
        pan_number: "",  // never pre-filled — PAN is not returned by the API
        gst_number: user.gst_number || "",
        gst_address: user.gst_address || "",
        advance_non_refundable: !!user.advance_non_refundable,
        guest_whatsapp_paused: !!user.guest_whatsapp_paused,
        upi_id: user.upi_id || "",
        upi_qr_url: user.upi_qr_url || "",
        company_name: user.company_name || "",
        same_day_cutoff_time: user.same_day_cutoff_time || "",
        notification_prefs: user.notification_prefs || { emi: true, tax: true, maintenance: true, checkin: true },
      };
      setForm(initial);
      setOriginal(initial);
    }
  }, [user]);

  useEffect(() => {
    if (teamQuery.data?.defaults && !roleDefaults) {
      setRoleDefaults(teamQuery.data.defaults);
      applyRoleDefaults(inviteForm.role, teamQuery.data.defaults);
    }
    // Initialise the invite once; member refreshes must preserve a drafted invite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamQuery.data, roleDefaults]);

  useEffect(() => {
    if (!user?.is_staff) {
      api.get("/referrals").then(r => setReferralData(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    api.get("/settings/pricing")
      .then(r => setPricingSettings(s => ({ ...s, ...r.data })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/automations").then(r => {
      const d = r.data;
      setAutoForm(f => ({
        ...f,
        auto_create_cleaning_task: d.auto_create_cleaning_task ?? true,
        auto_review_request:       d.auto_review_request       ?? true,
        auto_checkin_48h:          d.auto_checkin_48h          ?? true,
        auto_checkin_day:          d.auto_checkin_day          ?? true,
        auto_checkout_day:         d.auto_checkout_day         ?? true,
        checkin_whatsapp:          d.checkin_whatsapp          ?? true,
        smart_reply_enabled:       d.smart_reply_enabled       ?? true,
        auto_daily_digest:         d.auto_daily_digest         ?? true,
        pricing_nudges_enabled:    d.pricing_nudges_enabled    ?? true,
        dnd_enabled:               d.dnd_enabled               ?? false,
        dnd_start:                 d.dnd_start                 || "23:00",
        dnd_end:                   d.dnd_end                   || "07:00",
      }));
    }).catch(() => {});
  }, []);

  const setAuto = (key, val) => setAutoForm(f => ({ ...f, [key]: val }));

  const saveAuto = async () => {
    setSavingAuto(true);
    try {
      await api.put("/automations", autoForm);
      toast.success("Automation settings saved");
    } catch { toast.error("Failed to save automation settings"); }
    finally { setSavingAuto(false); }
  };

  const savePricingSettings = async () => {
    setSavingPricing(true);
    try {
      await api.put("/settings/pricing", pricingSettings);
      toast.success("Pricing automation saved");
    } catch { toast.error("Failed to save pricing settings"); }
    finally { setSavingPricing(false); }
  };

  const handleEnable2FA = async () => {
    setTwoFaLoading(true);
    try {
      await api.post("/auth/2fa/send-setup-otp");
      setTwoFaSetup("awaiting_otp");
      toast.success("Verification code sent to " + user?.email);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send code");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (twoFaCode.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setTwoFaLoading(true);
    try {
      await api.post("/auth/2fa/enable", { code: twoFaCode });
      setTwoFaEnabled(true);
      setTwoFaSetup(null);
      setTwoFaCode("");
      setUser({ ...user, two_fa_enabled: true });
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired code");
      setTwoFaCode("");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setTwoFaLoading(true);
    try {
      await api.post("/auth/2fa/disable");
      setTwoFaEnabled(false);
      setUser({ ...user, two_fa_enabled: false });
      toast.success("Two-factor authentication disabled");
    } catch {
      toast.error("Failed to disable 2FA");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (form.gst_number && validateGSTIN(form.gst_number) !== "valid") {
      toast.error("Fix the GSTIN before saving — checksum is invalid");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", form);
      setUser(data);
      const saved = {
        name: data.name || "", phone: data.phone || "",
        pan_number: "",  // PAN not returned by API — always blank after save
        gst_number: data.gst_number || "", gst_address: data.gst_address || "", advance_non_refundable: !!data.advance_non_refundable, guest_whatsapp_paused: !!data.guest_whatsapp_paused, company_name: data.company_name || "",
        upi_id: data.upi_id || "",
        upi_qr_url: data.upi_qr_url || "",
        same_day_cutoff_time: data.same_day_cutoff_time || "",
        notification_prefs: data.notification_prefs || form.notification_prefs,
      };
      setOriginal(saved);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingQR(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url || `${API_BASE}/files/${data.storage_path}`;
      set("upi_qr_url", url);
    } catch {
      toast.error("Failed to upload QR code");
    } finally {
      setUploadingQR(false);
    }
  };

  const handleExport = (type) => {
    const token = localStorage.getItem("session_token");
    const url = `${API_BASE}/export/${type}`;
    const a = document.createElement("a");
    a.href = token ? `${url}?token=${token}` : url;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleInvite = async () => {
    if (sendingInvite || !roleDefaults) return;
    if (!inviteForm.email.trim() && !inviteForm.phone.trim()) { toast.error("Enter email or phone"); return; }
    setSendingInvite(true);
    setInviteUrl(null);
    try {
      const { data } = await api.post("/team/invite", { ...inviteForm, ...inviteAccess });
      const sent = [inviteForm.email && "email", inviteForm.phone && "WhatsApp"].filter(Boolean).join(" + ");
      toast.success(`Invite sent via ${sent || "link"}`);
      setInviteUrl(data.invite_url || null);
      setInviteForm({ email: "", phone: "", role: "co_host" });
      setCustomiseInvite(false);
      applyRoleDefaults("co_host");
      loadTeam();
    } catch (e) {
      const msg = e?.response?.data?.detail
        || (e?.response?.status ? `Server error ${e.response.status}` : null)
        || e?.message || "Failed to send invite";
      toast.error(msg);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (sendingInvite || !roleDefaults) return;
    if (!inviteForm.email.trim()) { toast.error("Enter the invitee's email to generate a link"); return; }
    setSendingInvite(true);
    setInviteUrl(null);
    try {
      const { data } = await api.post("/team/invite", { ...inviteForm, ...inviteAccess, link_only: true });
      const url = data.invite_url || "";
      await navigator.clipboard.writeText(url);
      setInviteUrl(url);
      toast.success("Invite link copied — paste it in SMS or WhatsApp");
      loadTeam();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate link");
    } finally { setSendingInvite(false); }
  };

  const handleRemoveMember = async (memberId) => {
    setRemovingId(memberId);
    try {
      await api.delete(`/team/members/${memberId}`);
      await loadTeam();
      toast.success("Team member removed");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove member");
      return false;
    } finally {
      setRemovingId(null);
    }
  };

  const saveMemberPhone = async (staffId, phone) => {
    try {
      await api.put(`/team/members/${staffId}`, { phone: (phone || "").trim() });
      await loadTeam();
    } catch { toast.error("Could not save the number"); }
  };

  const handleDeleteAccount = async () => {
    if (deleteEmail.trim().toLowerCase() !== user?.email?.toLowerCase()) {
      toast.error("Email doesn't match");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/account", { data: { confirm_email: deleteEmail } });
      toast.success("Account deleted");
      setTimeout(() => { window.location.href = "/login"; }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  const gstinStatus = validateGSTIN(form.gst_number);
  const panStatus   = validatePAN(form.pan_number);
  const gstinState  = gstinStatus === "valid" ? GSTIN_STATES[form.gst_number.slice(0, 2)] : null;

  const inputCls = "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1 focus:border-amber-400 focus:ring-amber-400 placeholder:text-slate-400 dark:placeholder:text-slate-500";

  return (
    <div className="space-y-6 max-w-2xl">
      <OnboardingBanner pageKey="settings" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-amber-400 tracking-tight">Settings</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage your profile, tax info, and preferences.</p>
        </div>
        {isDirty && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 font-medium">
            <Warning size={13} weight="fill" /> Unsaved changes
          </div>
        )}
      </div>

      {/* ── Owner Profile ── */}
      <SectionCard icon={<User size={16} weight="duotone" />} title="Owner Profile">
        <div className="flex items-center gap-4">
          {user?.picture && <img src={user.picture} alt="" className="w-12 h-12 rounded-full ring-2 ring-amber-200" />}
          <div>
            <p className="text-sm font-semibold text-slate-800 dark:text-white">{user?.name}</p>
            <p className="text-xs text-slate-500">{user?.email}</p>
          </div>
        </div>

        {user?.is_staff && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            These belong to the host's own account, so only they can change them. Ask them
            if something here needs updating.
          </p>
        )}

        {!user?.is_staff && (
        <FieldRow label="Display Name">
          <Input value={form.name} onChange={e => set("name", e.target.value)}
            className={inputCls} placeholder="Your name" />
        </FieldRow>
        )}

        {!user?.is_staff && (
        <FieldRow label="Company / Business Name">
          <Input value={form.company_name} onChange={e => set("company_name", e.target.value)}
            className={inputCls} placeholder="Optional — appears on invoices" />
        </FieldRow>
        )}

        {!user?.is_staff && (
        <FieldRow label="Same-day booking cutoff (all properties)" hint="After this time, guests can't book a same-day check-in on any property (no one to receive them at night). A property can override this in its own settings. Leave blank for no cutoff.">
          <div className="flex items-center gap-2">
            <Input type="time" value={form.same_day_cutoff_time} onChange={e => set("same_day_cutoff_time", e.target.value)}
              className={`${inputCls} w-40`} />
            {form.same_day_cutoff_time && (
              <button type="button" onClick={() => set("same_day_cutoff_time", "")}
                className="text-[11px] text-slate-400 hover:text-amber-500">Clear</button>
            )}
          </div>
        </FieldRow>
        )}

        {!user?.is_staff && (
        <FieldRow label="Contact Phone" hint="Shared with guests in check-in WhatsApp messages so they can reach you">
          <div className="relative">
            <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-400 pointer-events-none" />
            <Input
              value={form.phone}
              onChange={e => set("phone", e.target.value.replace(/[^\d+\-\s()]/g, "").slice(0, 15))}
              className={`${inputCls} pl-8`}
              placeholder="+91 98765 43210"
              type="tel"
              inputMode="tel"
            />
          </div>
        </FieldRow>
        )}

        {!user?.is_staff && (<>
        <Separator className="!my-2" />

        {/* PAN */}
        <FieldRow label="PAN Number" hint="Required for TDS credit (Form 26AS / AIS)">
          {user?.pan_number_set && !form.pan_number && (
            <p className="text-[11px] text-slate-500 mb-1.5 font-mono">
              Saved: <span className="font-semibold text-slate-700 dark:text-slate-300">{user.pan_number_masked}</span>
              <span className="ml-2 text-slate-400 font-sans">— enter a new one below to update</span>
            </p>
          )}
          <div className="relative">
            <Input
              value={form.pan_number}
              onChange={e => set("pan_number", e.target.value.toUpperCase().slice(0, 10))}
              className={`${inputCls} pr-8 font-mono uppercase`}
              placeholder={user?.pan_number_set ? "Enter new PAN to update" : "ABCDE1234F"}
              maxLength={10}
            />
            {form.pan_number && (
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 mt-0.5">
                {panStatus === "valid"
                  ? <CheckCircle size={15} weight="fill" className="text-green-500" />
                  : <Warning size={15} weight="fill" className="text-red-400" />
                }
              </span>
            )}
          </div>
          {form.pan_number && panStatus !== "valid" && (
            <p className="text-[11px] text-red-500 mt-1">{panStatus}</p>
          )}
        </FieldRow>

        {/* GSTIN */}
        <FieldRow label="GSTIN" hint="GST registration number — determines which state's CGST/SGST or IGST applies">
          <div className="relative">
            <Input
              value={form.gst_number}
              onChange={e => set("gst_number", e.target.value.toUpperCase().slice(0, 15))}
              className={`${inputCls} pr-8 font-mono uppercase`}
              placeholder="29AABCU9603R1ZX"
              maxLength={15}
            />
            {form.gst_number && (
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 mt-0.5">
                {gstinStatus === "valid"
                  ? <CheckCircle size={15} weight="fill" className="text-green-500" />
                  : <Warning size={15} weight="fill" className="text-red-400" />
                }
              </span>
            )}
          </div>
          {form.gst_number && gstinStatus !== "valid" && (
            <p className="text-[11px] text-red-500 mt-1">{gstinStatus}</p>
          )}
          {gstinState && (
            <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg px-2.5 py-1">
              <CheckCircle size={12} weight="fill" />
              Registered in <strong className="ml-0.5">{gstinState}</strong>
              <span className="ml-1 text-green-500">· Used to determine IGST vs CGST+SGST on direct bookings</span>
            </div>
          )}
          {!form.gst_number && (
            <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-slate-500 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg px-2.5 py-1">
              <Info size={12} />
              Not required unless annual turnover exceeds ₹20 lakhs
            </div>
          )}
        </FieldRow>

        {/* GST registered address — printed as the supplier address on tax invoices */}
        <FieldRow label="GST registered address" hint="The address on your GST registration. Printed as the supplier address on every tax invoice; leave blank if you are not registered">
          <textarea
            value={form.gst_address}
            onChange={e => set("gst_address", e.target.value.slice(0, 240))}
            rows={2}
            className={`${inputCls} resize-none`}
            placeholder="12, MG Road, Bengaluru, Karnataka 560001"
          />
        </FieldRow>

        {/* Printed on invoices that record an advance payment */}
        <FieldRow label="Advance is non-refundable" hint="Adds 'The advance paid is non-refundable' to every invoice that shows an advance payment">
          <label className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-amber-500"
              checked={!!form.advance_non_refundable}
              onChange={e => set("advance_non_refundable", e.target.checked)} />
            Print this on invoices
          </label>
        </FieldRow>

        {/* Stops every HostFolio WhatsApp to this host's guests; host alerts unaffected */}
        <FieldRow label="Pause messages to guests" hint="Stops every WhatsApp and email HostFolio sends your guests: confirmations, check-in and checkout messages, review requests, payment reminders and inbox replies. Your own alerts still arrive">
          <label className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-amber-500"
              checked={!!form.guest_whatsapp_paused}
              onChange={e => set("guest_whatsapp_paused", e.target.checked)} />
            Paused
          </label>
        </FieldRow>
        </>)}
      </SectionCard>

      {/* ── Connected Accounts ── */}
      <SectionCard icon={<LockSimple size={16} weight="duotone" />} title="Connected Accounts">
        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-100 dark:border-slate-600">
          <div className="flex items-center gap-3">
            {user?.picture
              ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              : <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center">
                  <GoogleLogo size={16} className="text-slate-500" />
                </div>
            }
            <div>
              <p className="text-sm font-medium text-slate-800 dark:text-white flex items-center gap-1.5">
                <GoogleLogo size={13} weight="bold" className="text-blue-500" /> Google
              </p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-semibold">Connected</span>
        </div>
        <p className="text-xs text-slate-400">Sign-in is managed via Google OAuth. Password-based login is not supported.</p>
      </SectionCard>

      {/* ── Connect WhatsApp ──
          Deliberately NOT owner-only: a team member needs their own number connected to
          get their own task alerts and digests, and to use the assistant under their own
          permissions rather than the host's. */}
      <SectionCard icon={<WhatsappLogo size={16} weight="duotone" />} title="WhatsApp">
        {waLink?.connected ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 dark:text-white">
                  Connected on +91 {waLink.phone}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                  Message us on WhatsApp to ask about your properties, bookings and rates,
                  and to change prices. You also get booking alerts and the daily digest here.
                </p>
              </div>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-semibold shrink-0">
                Connected
              </span>
            </div>
            <button
              onClick={waRemove}
              disabled={waBusy}
              className="min-h-[44px] px-4 py-2 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {waBusy ? "…" : "Disconnect"}
            </button>
          </div>
        ) : waSent ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-800 dark:text-white">
              We sent a 6-digit code on WhatsApp to <strong>+91 {waPhone}</strong>.
            </p>
            <InputOTP maxLength={6} value={waCode} onChange={setWaCode} autoFocus>
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map(i => <InputOTPSlot key={i} index={i} />)}
              </InputOTPGroup>
            </InputOTP>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={waVerify}
                disabled={waBusy || waCode.length !== 6}
                className="min-h-[44px] px-4 py-2 text-xs font-semibold rounded-lg bg-amber-400 hover:bg-amber-500 text-white transition-colors disabled:opacity-50"
              >
                {waBusy ? "…" : "Verify & Connect"}
              </button>
              <button
                onClick={() => { setWaSent(false); setWaCode(""); }}
                className="min-h-[44px] px-4 py-2 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Use a different number
              </button>
            </div>
            <p className="text-[11px] text-slate-400">The code expires in 10 minutes.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-400 leading-relaxed">
              Connect your WhatsApp to ask about your properties and change prices from chat,
              and to receive booking alerts and the daily digest. We send a code to confirm
              the number is yours.
            </p>
            <FieldRow label="WhatsApp number" hint="10 digit Indian mobile, without +91">
              <Input
                value={waPhone}
                onChange={e => setWaPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="9876543210"
                inputMode="numeric"
                className="max-w-[220px]"
              />
            </FieldRow>
            <button
              onClick={waStart}
              disabled={waBusy || waPhone.length !== 10}
              className="min-h-[44px] px-4 py-2 text-xs font-semibold rounded-lg bg-amber-400 hover:bg-amber-500 text-white transition-colors disabled:opacity-50"
            >
              {waBusy ? "Sending…" : "Send code"}
            </button>
          </div>
        )}
      </SectionCard>

      {/* ── Two-Factor Authentication ── */}
      {!user?.is_staff && (
        <SectionCard icon={<ShieldCheck size={16} weight="duotone" />} title="Two-Factor Authentication">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-800 dark:text-white">Email verification on sign-in</p>
              <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                When enabled, we'll email a 6-digit code to <strong className="text-slate-600 dark:text-slate-300">{user?.email}</strong> each time you sign in with your password.
              </p>
            </div>
            {twoFaEnabled ? (
              <button
                onClick={handleDisable2FA}
                disabled={twoFaLoading}
                className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
              >
                {twoFaLoading ? "…" : "Disable"}
              </button>
            ) : (
              <button
                onClick={handleEnable2FA}
                disabled={twoFaLoading || twoFaSetup === "awaiting_otp"}
                className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50"
              >
                {twoFaLoading ? "Sending…" : "Enable"}
              </button>
            )}
          </div>

          {twoFaEnabled && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl">
              <CheckCircle size={15} className="text-green-600 dark:text-green-400 shrink-0" weight="fill" />
              <p className="text-xs text-green-700 dark:text-green-400 font-medium">2FA is active — your account requires email verification on every sign-in.</p>
            </div>
          )}

          {twoFaSetup === "awaiting_otp" && (
            <div className="space-y-3 pt-1">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Enter the 6-digit code sent to <strong className="text-slate-700 dark:text-slate-200">{user?.email}</strong>
              </p>
              <div className="flex gap-3 items-center flex-wrap">
                <InputOTP maxLength={6} value={twoFaCode} onChange={setTwoFaCode} autoFocus>
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
                <button
                  onClick={handleVerify2FA}
                  disabled={twoFaLoading || twoFaCode.length !== 6}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-amber-400 hover:bg-amber-500 text-white transition-colors disabled:opacity-50"
                >
                  {twoFaLoading ? "…" : "Verify & Enable"}
                </button>
              </div>
              <button
                onClick={() => { setTwoFaSetup(null); setTwoFaCode(""); }}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </SectionCard>
      )}

      {/* ── Notification Preferences ── owner only: these save onto the host's account */}
      {!user?.is_staff && (
      <SectionCard icon={<Bell size={16} weight="duotone" />} title="Notification Preferences">
        {[
          { key: "emi",         label: "EMI Due Dates",        desc: "Reminders for upcoming mortgage/EMI payments" },
          { key: "tax",         label: "Tax Deadlines",        desc: "GST filing and income tax due dates" },
          { key: "maintenance", label: "Maintenance Follow-ups",desc: "Pending maintenance issue reminders" },
          { key: "checkin",     label: "Guest Check-ins",      desc: "Upcoming guest arrival notifications" },
        ].map((n, i, arr) => (
          <div key={n.key}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-800 dark:text-white font-medium">{n.label}</p>
                <p className="text-xs text-slate-500">{n.desc}</p>
              </div>
              <Switch
                checked={form.notification_prefs[n.key]}
                onCheckedChange={() => set("notification_prefs", { ...form.notification_prefs, [n.key]: !form.notification_prefs[n.key] })}
              />
            </div>
            {i < arr.length - 1 && <Separator className="!mt-3" />}
          </div>
        ))}
      </SectionCard>
      )}

      {/* ── Automations ── owner only */}
      {!user?.is_staff && <SectionCard icon={<Lightning size={16} weight="duotone" />} title="Automations">
        {[
          { key: "auto_create_cleaning_task", label: "Auto-create cleaning task", desc: "Create a cleaning task on the checkout day for every confirmed booking" },
          { key: "auto_checkin_48h",          label: "Check-in reminder (48h)",   desc: "Send guests a reminder email 2 days before arrival" },
          { key: "auto_checkin_day",          label: "Check-in day message",      desc: "Send a welcome message to guests on their arrival day" },
          { key: "auto_checkout_day",         label: "Checkout reminder",         desc: "Remind guests of checkout time on their departure day" },
          { key: "auto_review_request",       label: "Review request",            desc: "Ask guests for a review after they check out" },
          { key: "checkin_whatsapp",          label: "WhatsApp check-in messages",desc: "Send check-in details via WhatsApp (requires WhatsApp number in Integrations)" },
          { key: "smart_reply_enabled",       label: "Smart reply in inbox",      desc: "AI-suggested replies for incoming guest messages" },
          { key: "auto_daily_digest",         label: "Daily digest (8 AM)",       desc: "Morning summary of today's check-ins and check-outs via email + WhatsApp" },
          { key: "pricing_nudges_enabled",    label: "Smart Pricing alerts",      desc: "Daily suggestions to raise or lower prices (last-minute gaps, high-demand dates, low occupancy) — apply in one tap" },
        ].map((item, i, arr) => (
          <div key={item.key}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-800 dark:text-white font-medium">{item.label}</p>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
              <Switch
                checked={!!autoForm[item.key]}
                onCheckedChange={v => setAuto(item.key, v)}
              />
            </div>
            {i < arr.length - 1 && <Separator className="!mt-3" />}
          </div>
        ))}

        {/* Do Not Disturb */}
        <Separator />
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-800 dark:text-white font-medium">Do Not Disturb</p>
            <p className="text-xs text-slate-500">Pause all auto-messages during these hours</p>
          </div>
          <Switch checked={!!autoForm.dnd_enabled} onCheckedChange={v => setAuto("dnd_enabled", v)} />
        </div>
        {autoForm.dnd_enabled && (
          <div className="flex items-center gap-3 mt-2">
            <div className="flex-1">
              <Label className="text-slate-500 dark:text-slate-400 text-xs">From</Label>
              <input type="time" value={autoForm.dnd_start} onChange={e => setAuto("dnd_start", e.target.value)}
                className="mt-1 w-full bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-amber-400 outline-none" />
            </div>
            <div className="flex-1">
              <Label className="text-slate-500 dark:text-slate-400 text-xs">To</Label>
              <input type="time" value={autoForm.dnd_end} onChange={e => setAuto("dnd_end", e.target.value)}
                className="mt-1 w-full bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-amber-400 outline-none" />
            </div>
          </div>
        )}

        <div className="pt-2">
          <button onClick={saveAuto} disabled={savingAuto}
            className="bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl px-5 py-2 text-sm transition">
            {savingAuto ? "Saving…" : "Save Automations"}
          </button>
        </div>
      </SectionCard>}

      {/* ── Data Export ── */}
      <SectionCard icon={<DownloadSimple size={16} weight="duotone" />} title="Data Export">
        {[
          { type: "bookings",   label: "Bookings",   desc: "All booking records",         icon: <Receipt size={15} className="text-amber-500" /> },
          { type: "expenses",   label: "Expenses",   desc: "All expense entries",          icon: <FileText size={15} className="text-blue-500" /> },
          { type: "properties", label: "Properties", desc: "Property list with details",   icon: <Buildings size={15} className="text-green-500" /> },
          { type: "pnl",        label: "P&L Report", desc: "Profit & Loss statement PDF",  icon: <FileCsv size={15} className="text-purple-500" /> },
        ].map(e => (
          <div key={e.type} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-100 dark:border-slate-600">
            <div className="flex items-center gap-2.5">
              {e.icon}
              <div>
                <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{e.label}</p>
                <p className="text-xs text-slate-400">{e.desc}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {e.type === "pnl" ? (
                <button onClick={() => handleExport("pnl?format=pdf")}
                  className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 px-3 py-1.5 rounded-lg text-xs font-medium transition">
                  PDF
                </button>
              ) : (
                <>
                  <button onClick={() => handleExport(e.type)}
                    className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 px-3 py-1.5 rounded-lg text-xs font-medium transition">
                    CSV
                  </button>
                  {e.type === "expenses" && (
                    <button onClick={() => handleExport("expenses?format=pdf")}
                      className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 px-3 py-1.5 rounded-lg text-xs font-medium transition">
                      PDF
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        <p className="text-[11px] text-slate-400 pt-1">Exports include all data across all properties. For GST-specific exports, use the <a href="/gst" className="underline text-amber-600">GST Compliance</a> page.</p>
      </SectionCard>

      {/* ── Direct Payment (UPI) ── */}
      <SectionCard icon={<DeviceMobileCamera size={16} weight="duotone" />} title="Direct Payment (UPI)">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Your UPI ID and QR code are shown to guests on your booking page so they can pay you directly — no intermediary, zero fees.
        </p>
        <FieldRow label="UPI ID" hint="e.g. yourname@upi or yourname@okicici">
          <Input
            value={form.upi_id}
            onChange={e => set("upi_id", e.target.value.trim())}
            className={`${inputCls} font-mono`}
            placeholder="yourname@upi"
          />
        </FieldRow>
        <FieldRow label="UPI QR Code" hint="Optional fallback — when a UPI ID is set, guests see an auto-generated QR with the exact amount pre-filled">
          {form.upi_qr_url ? (
            <div className="flex items-start gap-3">
              <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-600 bg-white shrink-0">
                <img src={form.upi_qr_url} alt="UPI QR Code" className="w-full h-full object-contain p-1" />
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <label className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 cursor-pointer font-semibold">
                  {uploadingQR ? <Spinner size={12} className="animate-spin" /> : <UploadSimple size={12} weight="bold" />}
                  {uploadingQR ? "Uploading…" : "Replace"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleQRUpload} disabled={uploadingQR} />
                </label>
                <button
                  onClick={() => set("upi_qr_url", "")}
                  className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 font-semibold text-left"
                >
                  <X size={12} weight="bold" />Remove
                </button>
              </div>
            </div>
          ) : (
            <label className={`flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed rounded-xl cursor-pointer transition
              ${uploadingQR ? "border-amber-300 bg-amber-50/50" : "border-slate-200 dark:border-slate-600 hover:border-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-950/20"}`}>
              {uploadingQR
                ? <Spinner size={22} className="animate-spin text-amber-500" />
                : <>
                    <UploadSimple size={22} className="text-slate-300 dark:text-slate-500 mb-1" />
                    <span className="text-xs text-slate-400">Upload QR</span>
                  </>
              }
              <input type="file" accept="image/*" className="hidden" onChange={handleQRUpload} disabled={uploadingQR} />
            </label>
          )}
        </FieldRow>
        {(form.upi_id || form.upi_qr_url) && (
          <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl text-xs text-green-700 dark:text-green-400">
            <CheckCircle size={13} weight="fill" />
            Guests will see your UPI details on your booking page and can pay you directly.
          </div>
        )}
      </SectionCard>

      {/* ── Referral Program ── */}
      {!user?.is_staff && (
        <SectionCard icon={<Gift size={16} weight="duotone" />} title="Refer a Host">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Share your link with other Airbnb hosts. When they sign up and upgrade, you both earn <strong>1 month free</strong>.
          </p>
          {referralData ? (
            <>
              <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5">
                <span className="text-xs text-slate-600 dark:text-slate-300 flex-1 truncate font-mono select-all">
                  {referralData.referral_url}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(referralData.referral_url);
                    toast.success("Link copied!");
                  }}
                  className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/30 rounded-lg transition shrink-0"
                  title="Copy link"
                >
                  <Copy size={14} weight="bold" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center border border-amber-100 dark:border-amber-800">
                  <p className="text-2xl font-bold text-amber-600">{referralData.referred_count}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Hosts signed up</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 text-center border border-green-100 dark:border-green-800">
                  <p className="text-2xl font-bold text-green-600">{referralData.converted_count}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Upgraded (credits earned)</p>
                </div>
              </div>
            </>
          ) : (
            <div className="h-10 flex items-center"><Spinner size={16} className="animate-spin text-amber-400" /></div>
          )}
        </SectionCard>
      )}

      {/* ── Team Access ── */}
      {!user?.is_staff && (
        <SectionCard icon={<Users size={16} weight="duotone" />} title={`Team Access${teamMembers.length > 0 ? ` (${teamMembers.length})` : ""}`}>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Review your current team below. Only you can invite people, change their access or remove them.
          </p>

          <CurrentTeam
            members={teamMembers}
            properties={teamProperties}
            loading={teamQuery.isPending}
            error={teamQuery.isError}
            onRetry={loadTeam}
            onEdit={setEditingMember}
            onRemove={handleRemoveMember}
            removingId={removingId}
            onSavePhone={saveMemberPhone}
          />
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white pt-4 border-t border-slate-200 dark:border-slate-700">Invite someone</h3>
          {/* Invite form */}
          <div className="space-y-2">
            <div className="flex gap-2 flex-wrap">
              <Input
                type="email"
                placeholder="Email (optional)"
                value={inviteForm.email}
                onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                className={inputCls + " flex-1 min-w-0"}
                onKeyDown={e => e.key === "Enter" && handleInvite()}
              />
              <Input
                type="tel"
                placeholder="WhatsApp phone (optional)"
                value={inviteForm.phone}
                onChange={e => setInviteForm(f => ({ ...f, phone: e.target.value }))}
                className={inputCls + " flex-1 min-w-0"}
                onKeyDown={e => e.key === "Enter" && handleInvite()}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <select
                value={inviteForm.role}
                onChange={e => { const role = e.target.value; setInviteForm(f => ({ ...f, role })); applyRoleDefaults(role); }}
                className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 text-sm rounded-lg px-2.5 py-2 focus:outline-none focus:border-amber-400 flex-1 min-w-[140px]"
              >
                <option value="co_host">Co-host</option>
                <option value="manager">Manager</option>
                <option value="maintenance">Maintenance/Cleanup</option>
              </select>
              <button
                onClick={handleInvite}
                disabled={sendingInvite || !roleDefaults}
                className="flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold px-3.5 py-2 rounded-xl text-sm transition shrink-0"
              >
                {sendingInvite ? <Spinner size={14} className="animate-spin" /> : <UserPlus size={14} weight="bold" />}
                Send Invite
              </button>
              <button
                onClick={handleCopyInviteLink}
                disabled={sendingInvite || !roleDefaults}
                title="Generate invite link to paste in SMS or any chat"
                className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 disabled:opacity-50 text-slate-700 dark:text-slate-200 font-semibold px-3.5 py-2 rounded-xl text-sm transition shrink-0"
              >
                <Copy size={14} weight="bold" /> Copy Link
              </button>
            </div>
          </div>

          {/* Set access at invite time (host only) */}
          <div className="rounded-xl border border-slate-100 dark:border-slate-600 overflow-hidden">
            <button
              type="button"
              onClick={() => setCustomiseInvite(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
            >
              <span className="flex items-center gap-1.5">
                <Sliders size={13} weight="bold" className="text-amber-500" />
                {customiseInvite ? "Access for this invite" : "Set page access and revenue sharing for this invite"}
              </span>
              <span className="text-[10px] font-normal text-slate-400">
                {customiseInvite ? "Hide" : `Using ${inviteForm.role === "co_host" ? "Co-host" : inviteForm.role === "manager" ? "Manager" : "Maintenance"} defaults · click to change`}
              </span>
            </button>
            {customiseInvite && (
              <div className="p-3">
                <AccessGrid
                  compact
                  role={inviteForm.role}
                  permissions={inviteAccess.permissions}
                  onPermissions={perms => setInviteAccess(a => ({ ...a, permissions: perms }))}
                  showRevenue={inviteAccess.show_revenue}
                  onShowRevenue={v => setInviteAccess(a => ({ ...a, show_revenue: v }))}
                  properties={teamProperties}
                  assignedIds={inviteAccess.assigned_property_ids}
                  onToggleProperty={pid => setInviteAccess(a => ({
                    ...a,
                    assigned_property_ids: a.assigned_property_ids.includes(pid)
                      ? a.assigned_property_ids.filter(x => x !== pid)
                      : [...a.assigned_property_ids, pid],
                  }))}
                />
                <p className="text-[10px] text-slate-400 mt-3">You can change all of this later from the member's Edit access button.</p>
              </div>
            )}
          </div>
          {/* Copy invite link after sending */}
          {inviteUrl && (
            <div className="flex items-center gap-2 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-700">
              <p className="text-xs text-teal-700 dark:text-teal-300 flex-1 truncate font-mono">{inviteUrl}</p>
              <button
                onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success("Invite link copied!"); }}
                className="shrink-0 flex items-center gap-1 text-xs font-semibold text-teal-600 dark:text-teal-400 hover:text-teal-700 transition"
              >
                <Copy size={13} /> Copy
              </button>
            </div>
          )}


        </SectionCard>
      )}

      {/* ── Dynamic Pricing ── */}
      <SectionCard icon={<Lightning size={16} weight="duotone" />} title="Dynamic Pricing Automation">
        <div className="space-y-5">
          {/* Auto-apply */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Auto-apply AI suggestions</p>
              <p className="text-xs text-slate-400 mt-0.5">AI pricing suggestions are applied automatically to your property rate without manual approval.</p>
            </div>
            <Switch
              checked={pricingSettings.auto_apply_enabled}
              onCheckedChange={v => setPricingSettings(s => ({ ...s, auto_apply_enabled: v }))}
            />
          </div>
          {pricingSettings.auto_apply_enabled && (
            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800 rounded-xl p-3">
              <FieldRow label="Max change to auto-apply" hint="Suggestions that exceed this % change will require manual approval.">
                <div className="flex items-center gap-3 mt-1.5">
                  <input type="range" min={5} max={50} step={5}
                    value={pricingSettings.auto_apply_max_pct}
                    onChange={e => setPricingSettings(s => ({ ...s, auto_apply_max_pct: Number(e.target.value) }))}
                    className="flex-1 accent-amber-400 h-1.5" />
                  <span className="text-sm font-bold text-amber-600 w-10 text-right">{pricingSettings.auto_apply_max_pct}%</span>
                </div>
              </FieldRow>
            </div>
          )}

          <Separator />

          {/* Last-minute discount */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-2.5">
              <Timer size={16} className="text-blue-500 mt-0.5 shrink-0" weight="duotone" />
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Last-minute discount</p>
                <p className="text-xs text-slate-400 mt-0.5">Automatically discount unfilled nights as the check-in date approaches.</p>
              </div>
            </div>
            <Switch
              checked={pricingSettings.last_minute_enabled}
              onCheckedChange={v => setPricingSettings(s => ({ ...s, last_minute_enabled: v }))}
            />
          </div>
          {pricingSettings.last_minute_enabled && (
            <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl p-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Trigger window">
                  <div className="flex items-center gap-2 mt-1">
                    <input type="number" min={1} max={30}
                      value={pricingSettings.last_minute_days}
                      onChange={e => setPricingSettings(s => ({ ...s, last_minute_days: Number(e.target.value) }))}
                      className="w-full px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
                    <span className="text-xs text-slate-400 whitespace-nowrap">days</span>
                  </div>
                </FieldRow>
                <FieldRow label="Discount %">
                  <div className="flex items-center gap-2 mt-1">
                    <input type="number" min={5} max={50}
                      value={pricingSettings.last_minute_pct}
                      onChange={e => setPricingSettings(s => ({ ...s, last_minute_pct: Number(e.target.value) }))}
                      className="w-full px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                </FieldRow>
              </div>
              <p className="text-[11px] text-blue-600 dark:text-blue-400">
                Dates within {pricingSettings.last_minute_days} day{pricingSettings.last_minute_days !== 1 ? "s" : ""} of check-in will automatically show a {pricingSettings.last_minute_pct}% discount in your rate calendar.
              </p>
            </div>
          )}

          <button onClick={savePricingSettings} disabled={savingPricing}
            className="w-full py-2.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2">
            {savingPricing ? <Spinner size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
            Save Pricing Settings
          </button>
        </div>
      </SectionCard>

      {/* ── Save ── every field it writes is owner-only, so staff never see it */}
      {!user?.is_staff && (
      <button onClick={handleSave} disabled={saving || !isDirty}
        className="w-full bg-amber-400 text-white hover:bg-amber-500 disabled:opacity-40 font-semibold rounded-xl px-6 py-3 text-sm transition shadow-sm">
        {saving ? "Saving…" : isDirty ? "Save Changes" : "All changes saved ✓"}
      </button>
      )}

      {/* ── Danger Zone ── */}
      <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-red-100 dark:border-red-800 flex items-center gap-2">
          <Trash size={16} className="text-red-500" weight="duotone" />
          <h2 className="font-manrope text-sm font-semibold text-red-700 dark:text-red-400">Danger Zone</h2>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-300">Delete Account</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                Permanently deletes all your properties, bookings, expenses, and account data. This cannot be undone.
              </p>
            </div>
            {deleteStep === 0 && (
              <button onClick={() => setDeleteStep(1)}
                className="shrink-0 px-4 py-2 bg-white dark:bg-slate-800 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 text-sm font-semibold rounded-xl transition">
                Delete Account
              </button>
            )}
          </div>

          {deleteStep === 1 && (
            <div className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-700 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 rounded-lg px-3 py-2">
                <Warning size={13} weight="fill" />
                All data will be permanently deleted. This action cannot be reversed.
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Type your email address <strong className="font-mono">{user?.email}</strong> to confirm:
              </p>
              <Input
                value={deleteEmail}
                onChange={e => setDeleteEmail(e.target.value)}
                placeholder={user?.email}
                className="bg-slate-50 dark:bg-slate-700 border-red-200 dark:border-red-700 text-slate-800 dark:text-slate-100 focus:border-red-400"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteEmail.trim().toLowerCase() !== user?.email?.toLowerCase()}
                  className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white text-sm font-semibold rounded-xl py-2 transition">
                  {deleting ? "Deleting…" : "Permanently Delete Everything"}
                </button>
                <button onClick={() => { setDeleteStep(0); setDeleteEmail(""); }}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-xl transition">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Permission editor modal */}
      {editingMember && (
        <PermissionEditor
          member={editingMember}
          properties={teamProperties}
          onClose={() => setEditingMember(null)}
          onSaved={() => { loadTeam(); setEditingMember(null); }}
        />
      )}
    </div>
  );
}
